// @ts-check
import { createLog } from '../utils/logger'
import { TCString } from '@mol-ads/tcf-core'

const log = createLog()

/**
 * @template T
 */
export class Config {
  /**
   * @type {T}
   */
  #config
  /**
   * @param {T} config
   */
  constructor (config) {
    this.#config = config
  }

  /**
   * @template {keyof T} K
   * @overload
   * @param {K} key
   * @return {T[K]|void}
   */
  /**
   * @overload
   * @return {T}
   */
  /**
   * @template {keyof T} K
   * @param {K} [key]
   * @return {T[K]|T|void}
   */
  get (key) {
    if (!key) return this.#config
    
    if (Object.prototype.hasOwnProperty.call(this.#config, key)) {
      return this.#config[key]
    }
  }
}

/**
 * @param {ClientConfig} clientConfig
 * @return {CmpFromClientConfig}
 */
function getCmpConfigFromClientConfig (clientConfig) {
  const {
    backendUrl,
    brand,
    brokerId,
    controllerScriptBase,
    renderPlatform,
    showUi,
    uiLevel,
    uiScriptUrl,
    uuid,
    platform
  } = clientConfig

  return {
    backendUrl,
    brand,
    brokerId,
    controllerScriptBase,
    renderPlatform,
    showUi,
    uiLevel,
    uiScriptUrl,
    uuid,
    platform
  }
}

/**
 * @param {string|undefined} tcString 
 * @param {string|number} clientGvlUiVersion 
 * @return {{gvlVersion: number, gvlUiVersion: number}}
 */
function getGVLVersions (tcString, clientGvlUiVersion) {
  const { vendorListVersion } = tcString && TCString.decode(tcString) || {}
  log('gvlVersion from tcString', vendorListVersion)
  let gvlUiVersion = Number(clientGvlUiVersion)
  let gvlVersion = vendorListVersion || gvlUiVersion
  gvlVersion = Number(gvlVersion)
  gvlUiVersion = Math.max(gvlVersion, gvlUiVersion)
  log('selected gvlVersions', { gvlVersion, gvlUiVersion })
  return { gvlVersion, gvlUiVersion }
}

/**
 * @param {string|number|undefined} _nrvVersion
 * @param {string} nrvUiVersionFromClient
 * @return {{nrvVersion: number, nrvUiVersion: number}}
 */
function getNrvVersions (_nrvVersion, nrvUiVersionFromClient) {
  const nrvVersion = _nrvVersion ? Number(_nrvVersion) : Number(nrvUiVersionFromClient)
  let nrvUiVersion = parseInt(nrvUiVersionFromClient)
  nrvUiVersion = nrvVersion ? Math.max(nrvVersion, nrvUiVersion) : nrvUiVersion
  log('selected nrvVersions', { nrvVersion, nrvUiVersion })
  return { nrvVersion, nrvUiVersion }
}

/**
 * @param {string|number|undefined} _cvlVersion 
 * @param {string|number} cvlUiVersionFromClient
 * @return {{cvlVersion: string|number, cvlUiVersion: number}}
 */
function getCvlVersions (_cvlVersion, cvlUiVersionFromClient) {
  let cvlUiVersion = Number(cvlUiVersionFromClient)
  /** @type {string|number} */
  let cvlVersion
  // this is only necessary because the initial version '0.0.1', all following versions are integers
  if (_cvlVersion && !Number(_cvlVersion)) {
    cvlVersion = _cvlVersion
  } else if (cvlUiVersionFromClient && !Number(cvlUiVersionFromClient)) {
    cvlVersion = cvlUiVersionFromClient
  } else {
    cvlVersion = _cvlVersion ? Number(_cvlVersion) : cvlUiVersionFromClient ? Number(cvlUiVersionFromClient) : cvlUiVersion
    cvlUiVersion = cvlVersion ? Math.max(cvlVersion, cvlUiVersion) : cvlUiVersion
  }
  log('selected cvlVersions', { cvlVersion, cvlUiVersion })
  return { cvlVersion, cvlUiVersion }
}

/**
 * @param {OverrideableByStoredConfigItems} overrideableConfig
 * @param {ClientConfig} clientConfig
 * @return {{gvlVersion: number, gvlUiVersion: number, nrvVersion: number, nrvUiVersion: number, cvlVersion: string|number, cvlUiVersion: number}}
 */
function getVendorListVersions (overrideableConfig, clientConfig) {
  const { tcString, nrvVersion, cvlVersion } = overrideableConfig
  const { gvlUiVersion, nrvUiVersion, cvlUiVersion } = clientConfig
  return { 
    ...getGVLVersions(tcString, gvlUiVersion),
    ...getNrvVersions(nrvVersion, nrvUiVersion),
    ...getCvlVersions(cvlVersion, cvlUiVersion)
  }
}

/**
 * @param {ClientConfig} clientConfig 
 * @param {TCData|undefined} tcDataFromStorage 
 * @return {{nextRepromptVersion: number, repromptVersion: number}}
 */
function selectRepromptVersions (clientConfig, tcDataFromStorage) {
  const { 
    repromptVersion: currentRepromptVersionFromClient, 
    nextRepromptVersion: nextRepromptVersionFromClient 
  } = clientConfig
  const { repromptVersion: repromptVersionFromStorage = 0 } = tcDataFromStorage || {}
  const repromptVersion = Math.max(currentRepromptVersionFromClient, repromptVersionFromStorage)
  const nextRepromptVersion = Math.max(repromptVersion, nextRepromptVersionFromClient)
  log('nextRepromptVersion', nextRepromptVersion)
  return { nextRepromptVersion, repromptVersion }
}


/**
 * @param {ClientConfig} clientConfig 
 * @param {TCData|undefined} tcDataFromStorage 
 * @return {OverrideableByStoredConfigItems}
 */
function selectConfigBasedOnTCData (clientConfig, tcDataFromStorage) {
  const { 
    tcString: tcStringFromStorage = undefined,
    nrvString: nrvStringFromStorage = undefined,
    nrvVersion: nrvVersionFromStorage = undefined,
    addtlConsent: addtlConsentFromStorage = undefined,
    acmVersion: cvlVersionFromStorage,
    molGvlVersion: molGvlVersionFromStorage = undefined 
  } = tcDataFromStorage || {}

  const {
    tcString: tcStringFromClient,
    addtlConsent: addtlConsentFromClient,
    cvlVersion: cvlVersionFromClient, 
    nrvString: nrvStringFromClient, 
    nrvVersion: nrvVersionFromClient,
    molGvlVersion: molGvlVersionFromClient
  } = clientConfig || {}

  const repromptVersions = selectRepromptVersions(clientConfig, tcDataFromStorage)

  const configFromStorage = { 
    tcString: tcStringFromStorage,
    nrvString: nrvStringFromStorage,
    nrvVersion: nrvVersionFromStorage,
    addtlConsent: addtlConsentFromStorage,
    cvlVersion: cvlVersionFromStorage,
    molGvlVersion: molGvlVersionFromStorage,
    ...repromptVersions
  }
  const configFromClient = {
    tcString: tcStringFromClient,
    nrvString: nrvStringFromClient,
    nrvVersion: nrvVersionFromClient,
    addtlConsent: addtlConsentFromClient,
    cvlVersion: cvlVersionFromClient,
    molGvlVersion: molGvlVersionFromClient,
    ...repromptVersions
  }
  log('clientString', tcStringFromClient)
  log('storedString', tcStringFromStorage)
  const selectStrings = (tcStringFromStorage, tcStringFromClient) => {
    const stored = TCString.decode(tcStringFromStorage).lastUpdated
    const client = TCString.decode(tcStringFromClient).lastUpdated
    log('stored', stored)
    log('client', client)
    return stored > client ? configFromStorage : configFromClient
  }

  if (tcStringFromClient && tcStringFromStorage) {
    return selectStrings(tcStringFromStorage, tcStringFromClient)
  } else if (tcStringFromClient) {
    return configFromClient
  } else {
    return configFromStorage
  }
}

/**
 * @param {Config<ClientConfig>} config
 * @param {TCData} [tcData]
 * @return {Config<CMPConfig>}
 */
export function createCmpConfig (config, tcData) {
  const clientConfig = config.get()
  const overrideableConfig = selectConfigBasedOnTCData(clientConfig, tcData)
  const { molGvlVersion } = overrideableConfig
  const {gvlVersion: _gvlVersion, gvlUiVersion: _gvlUiVersion, ...vendorListVersions } = getVendorListVersions(overrideableConfig, clientConfig)
  const newUser = !clientConfig.tcString
  const existingUser = !!clientConfig.tcString
  const userMigratedToGvlVariant = molGvlVersion?.split('.').length === 3
  const { gvlEnvironment, geo, useGeoDeviceVariantGVL = false } = clientConfig
  const migrateUserGvl = useGeoDeviceVariantGVL && !userMigratedToGvlVariant && existingUser

  const setGvlVersionByMolGvlVersion = /** @type {(molGvlVersion: string) => string} */ (molGvlVersion => molGvlVersion?.replace(/\./g, '/'))
  const setGvlVersionByClientConfig = /** @type {(version: number) => string } */ ((version) => `${version}/${geo}/${gvlEnvironment}`)
  
  const gvlVersion = userMigratedToGvlVariant ? setGvlVersionByMolGvlVersion(molGvlVersion) : newUser && useGeoDeviceVariantGVL ? setGvlVersionByClientConfig(_gvlVersion) : _gvlVersion
  const migratedGvlVersion = !userMigratedToGvlVariant && useGeoDeviceVariantGVL && existingUser && setGvlVersionByClientConfig(_gvlVersion) || undefined
  const gvlUiVersion = userMigratedToGvlVariant || useGeoDeviceVariantGVL ? setGvlVersionByClientConfig(_gvlUiVersion) : _gvlUiVersion 

  /**
   * @type {Config<CMPConfig>}
   */
  return new Config({
    ...getCmpConfigFromClientConfig(clientConfig),
    ...overrideableConfig,
    ...vendorListVersions,
    gvlVersion,
    gvlUiVersion,
    migratedGvlVersion,
    existingUser,
    migrateUserGvl
  })
}

/**
 * @template {ClientConfig} T
 * @param {T} config 
 * @return {Config<T>}
 */
export function createClientConfig (config) {
  log('config', config)
  return new Config({ cmpModel: 'tcfv2', ...config })
}

/**
 * @typedef {CmpFromClientConfig &
 * Pick<OverrideableByStoredConfigItems,'tcString'|'addtlConsent'|'nrvString'|'nextRepromptVersion'|'repromptVersion'> &
 * Pick<VendorListVersions,'nrvVersion'|'nrvUiVersion'|'cvlVersion'|'cvlUiVersion'> &
 * {gvlVersion: string|number, gvlUiVersion: string|number, migratedGvlVersion: string|undefined, existingUser: boolean, migrateUserGvl: boolean}
 * } CMPConfig
 */

/**
 * @typedef {Object} VendorListVersions
 * @property {number} gvlVersion
 * @property {number} gvlUiVersion
 * @property {number} nrvVersion
 * @property {number} nrvUiVersion
 * @property {string|number} cvlVersion
 * @property {number} cvlUiVersion
 */

/**
 * @typedef {Omit<ClientConfig,'storeUserPreference'|'useGeoDeviceVariantGVL'|'geo'|'gvlEnvironment'> & {migratedGvlVersion: string|undefined, existingUser: boolean, migrateUserGvl: boolean, gvlUiVersion: string}} _CMPConfig
 */

/**
 * @typedef {Object} ClientConfig
 * @property {string} [addtlConsent]
 * @property {string} [backendUrl]
 * @property {string} [brand]
 * @property {string} [brokerId]
 * @property {string} [channel]
 * @property {string} controllerScriptBase
 * @property {string|number} cvlVersion
 * @property {string|number} cvlUiVersion
 * @property {string} [gvlEnvironment]
 * @property {string|number} gvlUiVersion
 * @property {string|number} gvlVersion
 * @property {number} nextRepromptVersion
 * @property {string} [renderPlatform]
 * @property {number} repromptVersion
 * @property {boolean|string} [showUi]
 * @property {boolean} [storeUserPreference]
 * @property {string} [tcString]
 * @property {UILevel} [uiLevel]
 * @property {string} uiScriptUrl
 * @property {string} [uuid]
 * @property {string} platform
 * @property {string} [nrvString]
 * @property {string} nrvUiVersion
 * @property {string|number} nrvVersion
 * @property {boolean} [useGeoDeviceVariantGVL]
 * @property {string} [molGvlVersion]
 * @property {string} [geo]
 * @property {'tcfv2'|'lgpd'} [cmpModel]
 */

/**
 * @typedef {'Level1' | 'Level2'} UILevel
 */

/**
 * @typedef {import('@mol-ads/tcf-cmpapi').TCData} TCData
 */

/**
 * @typedef {Object} OverrideableByStoredConfigItems
 * @property {string|undefined} tcString
 * @property {string|undefined} addtlConsent
 * @property {string|number|undefined} cvlVersion
 * @property {string|undefined} nrvString
 * @property {string|number|undefined} nrvVersion
 * @property {string|undefined} molGvlVersion
 * @property {number} nextRepromptVersion
 * @property {number} repromptVersion
 */



/**
 * @typedef {Pick<ClientConfig,
* 'backendUrl'|
* 'brand'|
* 'brokerId'|
* 'controllerScriptBase'|
* 'renderPlatform'|
* 'showUi'|
* 'uiLevel'|
* 'uiScriptUrl'|
* 'uuid'|
* 'platform'
* >} CmpFromClientConfig 
*/